import {
  format,
  startOfDay,
  formatDistance,
  isToday,
  isYesterday,
} from "date-fns";
import { groupBy, sortBy } from "lodash";
import { Log } from "src/types.g";

export interface GroupedLogs {
  [key: string]: Log[];
}

/**
 * Groups logs by date and sorts logs within each group by timestamp (most recent first).
 *
 * @param logs - Array of logs to group and sort
 * @returns Object with date keys mapping to sorted arrays of logs
 *
 * @example
 * Input: [
 *   { timestamp: "2024-01-15T10:00:00Z" },
 *   { timestamp: "2024-01-15T15:00:00Z" },
 *   { timestamp: "2024-01-14T12:00:00Z" }
 * ]
 * Output: {
 *   "2024-01-15": [
 *     { timestamp: "2024-01-15T15:00:00Z" },
 *     { timestamp: "2024-01-15T10:00:00Z" }
 *   ],
 *   "2024-01-14": [
 *     { timestamp: "2024-01-14T12:00:00Z" }
 *   ]
 * }
 */
export const groupAndSortLogsByDate = (logs: Log[]): GroupedLogs => {
  const grouped = groupBy(logs, (log) => {
    const date = new Date(log.timestamp);
    return format(startOfDay(date), "yyyy-MM-dd");
  });

  // Sort logs within each group by timestamp (descending - most recent first)
  Object.keys(grouped).forEach((key) => {
    grouped[key] = sortBy(grouped[key], (log) =>
      new Date(log.timestamp).getTime(),
    ).reverse();
  });

  return grouped;
};

/**
 * Generates a human-readable date header for a timestamp.
 * Returns "Today", "Yesterday", or a relative time description.
 *
 * @param timestamp - ISO timestamp string (e.g., "2024-01-15T10:00:00Z")
 * @returns Human-readable date header (e.g., "Today", "Yesterday", "2 days ago")
 */
export const getDateHeader = (timestamp: string): string => {
  const date = new Date(timestamp);

  if (isToday(date)) {
    return "Today";
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  return formatDistance(date, new Date(), { addSuffix: true });
};

export const areLogsEqual = (a: Log[], b: Log[]) =>
  a.length === b.length && a.every((log, i) => log.id === b[i].id);
